import { hasPublisherDocumentAccess } from '../renderEnv.js';
import {
  dispatchPublisherEvent,
  lifecycleDetail,
  NPLUSONE_EVENT,
} from '../lifecycle.js';

export const CONTENT_LOCK_GATE = Object.freeze({
  Timed: 'timed',
  Required: 'required',
});

const READY_TIMEOUT_MS = 15_000;

/**
 * Publisher content boundary controlled by a timed or completion-required ad.
 * Selector resolution is read-only. Locked content is CLIPPED out of the
 * layout at the gate (paywall-style truncation — never an overlay on live
 * content), and only after a banner renders synchronously or a matching
 * Lucid instance announces readiness.
 */
export class ContentLockContainer {
  constructor(config, renderEnv) {
    this._config = config;
    this._renderEnv = renderEnv;
    this._pageWindow = renderEnv?.topWindow ?? window;
    this._document = this._pageWindow.document;
    this._root = null;
    this._boundary = null;
    this._insertBefore = false;
    this._gate = null;
    this._wrapper = null;
    this._instanceId = null;
    this._sourceId = null;
    this._placementId = null;
    this._log = null;
    this._rendered = false;
    this._ready = false;
    this._active = false;
    this._terminal = false;
    this._listeners = [];
    this._readyTimer = null;
    this._countdownTimer = null;
    this._continueButton = null;
    this._lockedTease = null;
    this._gateNotice = null;
    this._savedRootStyle = null;
    this._onAlternateRequested = null;
    this._resizeObserver = null;
    this._onWindowResize = null;
  }

  isSupported() {
    if (!hasPublisherDocumentAccess(this._renderEnv)) return false;

    this._gate = parseGate(this._config?.gate);
    if (!this._gate || !validConfig(this._config)) return false;

    const contentSelector = normalizeOptionalSelector(this._config.content_selector);
    const boundarySelector = normalizeOptionalSelector(this._config.boundary_selector);
    let root = contentSelector ? queryOne(this._document, contentSelector) : null;
    if (contentSelector && !root) return false;

    if (boundarySelector) {
      const boundary = queryOne(this._document, boundarySelector);
      if (!boundary) return false;
      if (root && !root.contains(boundary)) return false;
      root = root ?? inferRootForBoundary(boundary, this._config.item_selector);
      if (!root || root === boundary || !root.contains(boundary)) return false;
      if (!hasFollowingContent(root, boundary)) return false;
      this._root = root;
      this._boundary = boundary;
      this._insertBefore = false;
      return true;
    }

    const resolved = root
      ? resolveBoundaryInRoot(root, this._config.item_selector, this._config.visible_item_count)
      : inferRootAndBoundary(
        this._document,
        this._config.item_selector,
        this._config.visible_item_count,
      );
    if (!resolved) return false;

    this._root = resolved.root;
    this._boundary = resolved.boundary;
    this._insertBefore = resolved.insertBefore;
    return true;
  }

  get renderLocation() {
    if (!this._boundary) throw new Error('ContentLock boundary was not resolved');
    return this._boundary;
  }

  get debugInfo() {
    return {
      gate: this._gate?.type ?? null,
      content: describeElement(this._root),
      boundary: describeElement(this._boundary),
      insertion: this._insertBefore ? 'before' : 'after',
    };
  }

  applyStyles(_el) {}

  insertWrapper(wrapper) {
    if (!this._boundary?.parentNode) {
      throw new Error('ContentLock boundary is no longer attached');
    }
    const reference = this._insertBefore ? this._boundary : this._boundary.nextSibling;
    this._boundary.parentNode.insertBefore(wrapper, reference);
  }

  prepareLifecycle({
    instanceId,
    sourceId,
    placementId,
    wrapper,
    log,
    onAlternateRequested = null,
  }) {
    this._instanceId = instanceId;
    this._sourceId = sourceId;
    this._placementId = placementId;
    this._wrapper = wrapper;
    this._log = log;
    this._onAlternateRequested = onAlternateRequested;

    this._listen(NPLUSONE_EVENT.Ready, (detail) => {
      this._ready = true;
      if (this._rendered) this._activate();
    });
    this._listen(NPLUSONE_EVENT.Completed, (detail) => {
      this._unlock(NPLUSONE_EVENT.Completed, detail.conversation_id ?? null);
    });
    this._listen(NPLUSONE_EVENT.Failed, (detail) => {
      this._unlock(NPLUSONE_EVENT.Failed, detail.conversation_id ?? null);
    });
    this._listen(NPLUSONE_EVENT.AlternateRequested, () => {
      if (this._gate.type !== CONTENT_LOCK_GATE.Required
        || !this._sourceId
        || !this._active
        || this._terminal
        || typeof this._onAlternateRequested !== 'function') return;
      Promise.resolve(this._onAlternateRequested()).catch((error) => {
        this._log?.warn('alternate survey request failed', error);
      });
    });
  }

  /** Transfer the active gate to a replacement that already emitted ready. */
  adoptReplacement({ instanceId, sourceId, wrapper }) {
    if (this._terminal || !this._active || !sourceId) return false;
    this._instanceId = instanceId;
    this._sourceId = sourceId;
    this._wrapper = wrapper;
    this._rendered = true;
    this._ready = true;
    this._resizeObserver?.observe(wrapper);
    this._refreshConcealment();
    this._log?.debug('content lock adopted alternate survey', {
      instance_id: instanceId,
      source_id: sourceId,
    });
    return true;
  }

  /**
   * Render a candidate invisibly beside the active survey, then swap only
   * after that exact Lucid instance announces readiness. Failure leaves the
   * current survey and gate untouched.
   */
  stageReplacement({ instanceId, sourceId, wrapper, render }) {
    if (this._terminal
      || !this._active
      || this._gate.type !== CONTENT_LOCK_GATE.Required
      || !sourceId) return Promise.resolve(false);

    wrapper.style.display = 'none';
    this.insertWrapper(wrapper);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ready) => {
        if (settled) return;
        settled = true;
        this._pageWindow.removeEventListener(NPLUSONE_EVENT.Ready, onReady);
        this._pageWindow.removeEventListener(NPLUSONE_EVENT.Failed, onFailed);
        this._pageWindow.clearTimeout(timeout);

        if (!ready || this._terminal) {
          wrapper.remove();
          resolve(false);
          return;
        }

        const previousWrapper = this._wrapper;
        wrapper.style.display = '';
        previousWrapper?.remove();
        resolve(this.adoptReplacement({ instanceId, sourceId, wrapper }));
      };
      const matches = (event) => event?.detail?.instance_id === instanceId;
      const onReady = (event) => {
        if (matches(event)) finish(true);
      };
      const onFailed = (event) => {
        if (matches(event)) finish(false);
      };
      const timeout = this._pageWindow.setTimeout(() => finish(false), READY_TIMEOUT_MS);

      this._pageWindow.addEventListener(NPLUSONE_EVENT.Ready, onReady);
      this._pageWindow.addEventListener(NPLUSONE_EVENT.Failed, onFailed);
      try {
        render();
      } catch (error) {
        this._log?.warn('alternate survey render failed', error);
        finish(false);
      }
    });
  }

  markRendered() {
    this._rendered = true;

    // source_id is explicit only for a direct Chattable winner. Banner gates
    // can activate immediately; Lucid gates wait for the matching ready event.
    if (!this._sourceId) {
      if (this._gate.type === CONTENT_LOCK_GATE.Required) {
        this._log?.warn('required content lock received a non-conversational bid — leaving content open');
        this._terminal = true;
        this._cleanup();
        return;
      }
      this._activate();
      return;
    }

    if (this._ready) {
      this._activate();
      return;
    }

    this._readyTimer = this._pageWindow.setTimeout(() => {
      if (this._ready || this._terminal) return;
      this._terminal = true;
      this._log?.warn('content lock creative did not become ready — leaving content open');
      dispatchPublisherEvent(
        this._renderEnv,
        NPLUSONE_EVENT.Failed,
        lifecycleDetail(this._placementId, this._instanceId),
      );
      this._cleanup();
    }, READY_TIMEOUT_MS);
  }

  abort() {
    this._restoreContent();
    this._terminal = true;
    this._cleanup();
  }

  _listen(name, handler) {
    const listener = (event) => {
      const detail = event?.detail;
      if (!detail || detail.instance_id !== this._instanceId) return;
      handler(detail);
    };
    this._pageWindow.addEventListener(name, listener);
    this._listeners.push([name, listener]);
  }

  _activate() {
    if (this._active || this._terminal || !this._root || !this._wrapper) return;
    this._active = true;
    this._addGateNotice();
    this._clipContent();
    this._log?.debug('content lock activated', this.debugInfo);
  }

  /**
   * "Before you continue" notice between the last visible paragraph and
   * the ad, matching the lucid content-lock demo's gate-transition: the
   * next paragraph blurred behind a translucent bar (lock icon ring +
   * kicker + title + gate-specific explanation), so the gate announces
   * itself instead of the article just stopping.
   */
  _addGateNotice() {
    const accent = '#3f4349';
    const container = this._document.createElement('div');
    container.setAttribute('data-nplusone-gate-notice', '');
    Object.assign(container.style, {
      position: 'relative',
      width: '100%',
      maxWidth: '696px',
      height: '110px',
      margin: '30px auto 14px',
      overflow: 'hidden',
      color: '#171813',
    });

    const preview = this._document.createElement('p');
    preview.setAttribute('aria-hidden', 'true');
    preview.textContent = previewCopyAfter(this._wrapper);
    Object.assign(preview.style, {
      position: 'absolute',
      inset: '-14px 0',
      margin: '0',
      filter: 'blur(4px)',
      opacity: '0.42',
      font: '17px/1.75 Georgia, "Times New Roman", serif',
      userSelect: 'none',
    });

    const computed = this._pageWindow.getComputedStyle(this._root);
    const background = resolvedBackground(computed.backgroundColor, this._document);
    const bar = this._document.createElement('div');
    Object.assign(bar.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '15px 18px',
      boxSizing: 'border-box',
      background: withAlpha(background, 0.91),
      borderTop: '1px solid #aaa9a2',
      borderBottom: '1px solid #aaa9a2',
      backdropFilter: 'blur(5px)',
      webkitBackdropFilter: 'blur(5px)',
    });

    const icon = this._document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    Object.assign(icon.style, {
      display: 'grid',
      placeItems: 'center',
      width: '38px',
      height: '38px',
      flex: '0 0 38px',
      color: accent,
      background: '#ffffff',
      border: '1px solid rgba(63, 67, 73, 0.24)',
      borderRadius: '50%',
    });
    icon.append(createLockIcon(this._document, { width: '18px', strokeWidth: '1.7' }));

    const copy = this._document.createElement('div');
    copy.style.minWidth = '0';
    const kicker = this._document.createElement('span');
    kicker.textContent = 'Continue reading';
    Object.assign(kicker.style, {
      display: 'block',
      marginBottom: '4px',
      color: '#31343a',
      font: '800 9px/1 Inter, ui-sans-serif, system-ui, sans-serif',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    });
    const title = this._document.createElement('strong');
    title.textContent = 'Before you continue';
    Object.assign(title.style, {
      display: 'block',
      font: '500 21px/1.08 Georgia, "Times New Roman", serif',
      letterSpacing: '-0.03em',
    });
    const detail = this._document.createElement('span');
    detail.textContent = this._gate.type === CONTENT_LOCK_GATE.Required
      ? 'Please answer a few quick questions to unlock the rest of this story.'
      : 'The rest of this story unlocks after a short message from our sponsor.';
    Object.assign(detail.style, {
      display: 'block',
      marginTop: '3px',
      color: '#55564e',
      font: '12px/1.4 Inter, ui-sans-serif, system-ui, sans-serif',
    });
    copy.append(kicker, title, detail);

    bar.append(icon, copy);
    container.append(preview, bar);
    this._wrapper.parentNode.insertBefore(container, this._wrapper);
    this._gateNotice = container;
  }

  /**
   * Conceal by CLIPPING the article at the gate: the locked portion leaves
   * the layout entirely (the visible page ends at the gate chrome), the way
   * mainstream paywalls truncate a story. Never an overlay on live content —
   * covering rendered ads reads as hidden-ad fraud to verification vendors,
   * and a full-height veiled page lets lazy-loading ad stacks keep firing
   * auctions for slots nobody can see. Clipping removes the scroll runway,
   * so below-gate lazy slots never even request.
   */
  _clipContent() {
    this._savedRootStyle = {
      maxHeight: this._root.style.maxHeight,
      overflow: this._root.style.overflow,
    };
    this._root.setAttribute('data-nplusone-content-locked', '');
    this._addLockedTease();
    this._refreshConcealment();

    // The clip point depends on live layout above and inside the gate.
    // Lazy images, web fonts, and the ad's own growth all land AFTER
    // activation and would otherwise shift the boundary under a stale
    // measurement (swallowing content that should stay visible).
    // Re-measure on any size change of the gate elements plus window
    // resize/load.
    if (typeof this._pageWindow.ResizeObserver === 'function') {
      this._resizeObserver = new this._pageWindow.ResizeObserver(() => {
        this._refreshConcealment();
      });
      this._resizeObserver.observe(this._wrapper);
      this._resizeObserver.observe(this._lockedTease);
    }
    this._onWindowResize = () => this._refreshConcealment();
    this._pageWindow.addEventListener('resize', this._onWindowResize);
    this._pageWindow.addEventListener('load', this._onWindowResize);
  }

  /**
   * The visible page must not end on a hard stop: after the ad, tease the
   * next locked paragraph blurred under a fade-to-background scrim so the
   * reader can SEE the story continues. The gate control renders centered
   * on the scrim (Continue pill for timed, lock label for required); the
   * clip lands after this strip.
   */
  _addLockedTease() {
    const tease = this._document.createElement('div');
    tease.setAttribute('data-nplusone-locked-tease', '');

    const computed = this._pageWindow.getComputedStyle(this._root);
    const background = resolvedBackground(computed.backgroundColor, this._document);
    const copy = this._document.createElement('p');
    copy.setAttribute('aria-hidden', 'true');
    // Skip the paragraph the notice already teases so the two strips
    // don't read as a duplicated glitch.
    copy.textContent = previewCopyAfter(this._wrapper, 1);
    Object.assign(copy.style, {
      margin: '0',
      filter: 'blur(4px)',
      opacity: '0.55',
      font: '19px/1.72 Georgia, "Times New Roman", serif',
      userSelect: 'none',
    });

    const scrim = this._document.createElement('div');
    Object.assign(scrim.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      color: computed.color || '#55564e',
      background: `linear-gradient(to bottom, ${withAlpha(background, 0.15)}, ${background} 77%)`,
      font: '700 9px/1.3 Inter, ui-sans-serif, system-ui, sans-serif',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    });
    if (this._gate.type === CONTENT_LOCK_GATE.Required) {
      const label = this._document.createElement('span');
      label.textContent = 'Answer a few quick questions to keep reading';
      scrim.append(createLockIcon(this._document), label);
    }
    if (this._gate.type === CONTENT_LOCK_GATE.Timed) {
      scrim.append(this._buildTimedContinue());
    }

    Object.assign(tease.style, {
      position: 'relative',
      width: '100%',
      maxWidth: '696px',
      minHeight: '150px',
      margin: '26px auto 0',
      overflow: 'hidden',
    });
    tease.append(copy, scrim);
    this._wrapper.parentNode.insertBefore(tease, this._wrapper.nextSibling);
    this._lockedTease = tease;
  }

  _buildTimedContinue() {
    const button = this._document.createElement('button');
    button.type = 'button';
    button.className = 'nplusone-content-continue';
    button.disabled = true;
    Object.assign(button.style, {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '11px 26px',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '999px',
      background: '#ffffff',
      color: '#17191c',
      font: '600 13.5px/1 Inter, ui-sans-serif, system-ui, sans-serif',
      letterSpacing: '0.01em',
      textTransform: 'none',
      boxShadow: '0 8px 22px rgba(0, 0, 0, 0.12)',
      opacity: '0.75',
      cursor: 'default',
      transition: 'opacity 0.25s ease',
    });
    button.setAttribute('aria-live', 'polite');
    this._continueButton = button;

    let remaining = this._gate.delaySeconds;
    const paint = () => {
      if (remaining <= 0) {
        button.disabled = false;
        button.textContent = 'Continue reading';
        button.style.cursor = 'pointer';
        button.style.opacity = '1';
        return;
      }
      button.textContent = `Continue reading in ${remaining}s`;
    };
    paint();

    if (remaining > 0) {
      this._countdownTimer = this._pageWindow.setInterval(() => {
        remaining -= 1;
        paint();
        if (remaining <= 0) {
          this._pageWindow.clearInterval(this._countdownTimer);
          this._countdownTimer = null;
        }
      }, 1000);
    }

    button.addEventListener('click', () => {
      if (button.disabled) return;
      this._unlock(NPLUSONE_EVENT.Skipped, null, true);
    });
    return button;
  }

  _refreshConcealment() {
    if (!this._root || !this._wrapper || !this._active) return;
    const rootRect = this._root.getBoundingClientRect();
    const finalElement = this._lockedTease ?? this._wrapper;
    const finalRect = finalElement.getBoundingClientRect();
    const measuredBottom = finalRect.bottom || this._wrapper.getBoundingClientRect().bottom;
    const visibleHeight = Math.max(0, measuredBottom - rootRect.top + this._root.scrollTop);
    this._root.style.maxHeight = `${Math.ceil(visibleHeight)}px`;
    this._root.style.overflow = 'hidden';
  }

  _unlock(eventName, conversationId, publish = false) {
    if (this._terminal) return;
    this._terminal = true;
    const scrollX = this._pageWindow.scrollX;
    const scrollY = this._pageWindow.scrollY;
    this._restoreContent();
    // Removing the article clip increases the document height. Browsers can
    // otherwise keep a reader who was at the temporary bottom pinned to the
    // newly expanded bottom, which looks like an unsolicited page jump. The
    // second restore runs after Lucid replaces its focused completion control.
    this._root?.getBoundingClientRect();
    this._pageWindow.scrollTo(scrollX, scrollY);
    this._pageWindow.setTimeout(() => {
      this._pageWindow.requestAnimationFrame(() => {
        this._pageWindow.scrollTo(scrollX, scrollY);
      });
    }, 0);
    this._log?.debug('content lock released', { outcome: eventName, instance_id: this._instanceId });
    if (publish) {
      dispatchPublisherEvent(
        this._renderEnv,
        eventName,
        lifecycleDetail(this._placementId, this._instanceId, conversationId),
      );
    }
    this._cleanup();
  }

  _restoreContent() {
    if (this._root && this._savedRootStyle) {
      this._root.style.maxHeight = this._savedRootStyle.maxHeight;
      this._root.style.overflow = this._savedRootStyle.overflow;
      this._root.removeAttribute('data-nplusone-content-locked');
    }
    this._continueButton?.remove();
    this._continueButton = null;
    this._lockedTease?.remove();
    this._lockedTease = null;
    this._gateNotice?.remove();
    this._gateNotice = null;
    this._active = false;
  }

  _cleanup() {
    for (const [name, listener] of this._listeners) {
      this._pageWindow.removeEventListener(name, listener);
    }
    this._listeners = [];
    if (this._readyTimer !== null) this._pageWindow.clearTimeout(this._readyTimer);
    if (this._countdownTimer !== null) this._pageWindow.clearInterval(this._countdownTimer);
    this._readyTimer = null;
    this._countdownTimer = null;
    this._onAlternateRequested = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    if (this._onWindowResize) {
      this._pageWindow.removeEventListener('resize', this._onWindowResize);
      this._pageWindow.removeEventListener('load', this._onWindowResize);
      this._onWindowResize = null;
    }
  }
}

function parseGate(gate) {
  if (gate?.type === CONTENT_LOCK_GATE.Required) return { type: CONTENT_LOCK_GATE.Required };
  const delay = gate?.config?.delay_seconds;
  if (
    gate?.type === CONTENT_LOCK_GATE.Timed
    && Number.isInteger(delay)
    && delay >= 0
  ) {
    return { type: CONTENT_LOCK_GATE.Timed, delaySeconds: delay };
  }
  return null;
}

function validConfig(config) {
  return config
    && validOptionalSelector(config.content_selector)
    && validOptionalSelector(config.boundary_selector)
    && typeof config.item_selector === 'string'
    && config.item_selector.trim().length > 0
    && Number.isInteger(config.visible_item_count)
    && config.visible_item_count >= 0;
}

function validOptionalSelector(value) {
  return value === null || (typeof value === 'string' && value.trim().length > 0);
}

function normalizeOptionalSelector(value) {
  return typeof value === 'string' ? value.trim() : null;
}

function queryOne(scope, selector) {
  try {
    const result = scope.querySelector(selector);
    return result?.nodeType === 1 ? result : null;
  } catch (_) {
    return null;
  }
}

function queryItems(root, selector) {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch (_) {
    return [];
  }
}

function resolveBoundaryInRoot(root, itemSelector, visibleItemCount) {
  const items = queryItems(root, itemSelector);
  if (items.length <= visibleItemCount) return null;
  return {
    root,
    boundary: items[visibleItemCount === 0 ? 0 : visibleItemCount - 1],
    insertBefore: visibleItemCount === 0,
  };
}

function inferRootAndBoundary(doc, itemSelector, visibleItemCount) {
  for (const selector of ['article', '[role="main"]', 'main']) {
    for (const root of Array.from(doc.querySelectorAll(selector))) {
      const resolved = resolveBoundaryInRoot(root, itemSelector, visibleItemCount);
      if (resolved) return resolved;
    }
  }

  const items = queryItems(doc, itemSelector);
  for (const item of items) {
    for (let root = item.parentElement; root && root !== doc.body; root = root.parentElement) {
      const group = queryItems(root, itemSelector);
      if (group[0] !== item || group.length <= visibleItemCount) continue;
      return resolveBoundaryInRoot(root, itemSelector, visibleItemCount);
    }
  }
  return null;
}

function inferRootForBoundary(boundary, itemSelector) {
  const semantic = boundary.closest('article, [role="main"], main');
  if (semantic) return semantic;
  for (let root = boundary.parentElement; root && root !== boundary.ownerDocument.body; root = root.parentElement) {
    if (hasFollowingContent(root, boundary) || queryItems(root, itemSelector).length > 1) return root;
  }
  return null;
}

function hasFollowingContent(root, boundary) {
  for (let node = boundary; node && node !== root; node = node.parentNode) {
    for (let sibling = node.nextSibling; sibling; sibling = sibling.nextSibling) {
      if (sibling.nodeType === 1) return true;
      if (sibling.nodeType === 3 && sibling.textContent.trim()) return true;
    }
  }
  return false;
}

function describeElement(element) {
  if (!element) return null;
  return `<${element.tagName.toLowerCase()}>${element.id ? `#${element.id}` : ''}`;
}

/**
 * First readable text after the gate, used as the blurred tease behind
 * the notice bar so the reader can SEE the story continues.
 */
function previewCopyAfter(wrapper, skip = 0) {
  let remaining = skip;
  for (let element = wrapper.nextElementSibling; element; element = element.nextElementSibling) {
    const text = element.textContent?.replace(/\s+/g, ' ').trim();
    if (!text) continue;
    if (remaining > 0) {
      remaining -= 1;
      continue;
    }
    return text.length > 240 ? `${text.slice(0, 237)}…` : text;
  }
  return 'The rest of this story continues below with more reporting and perspective.';
}

/**
 * Add an alpha channel to a computed `rgb(...)` color; anything else
 * (named colors, already-transparent values) falls back to a white veil.
 */
function withAlpha(color, alpha) {
  const match = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(color);
  if (!match) return `rgba(255, 255, 255, ${alpha})`;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

function resolvedBackground(value, doc) {
  if (value && value !== 'transparent' && value !== 'rgba(0, 0, 0, 0)') return value;
  const bodyBackground = doc.defaultView?.getComputedStyle(doc.body).backgroundColor;
  return bodyBackground && bodyBackground !== 'rgba(0, 0, 0, 0)' ? bodyBackground : '#fff';
}

function createLockIcon(doc, { width = '20px', strokeWidth = '1.5' } = {}) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = doc.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  Object.assign(svg.style, {
    width,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  });
  const body = doc.createElementNS(ns, 'rect');
  body.setAttribute('x', '5');
  body.setAttribute('y', '10');
  body.setAttribute('width', '14');
  body.setAttribute('height', '11');
  body.setAttribute('rx', '3');
  const shackle = doc.createElementNS(ns, 'path');
  shackle.setAttribute('d', 'M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10');
  svg.append(body, shackle);
  return svg;
}
